import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, Table, Button, Tag } from 'antd';
import { BankOutlined, CalendarOutlined, CheckSquareOutlined, WalletOutlined, FileTextOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const TeamMemberDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hostels, setHostels] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ hostels: 0, bookings: 0, tasks: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'employee' && user?.employeeType !== 'accountant') {
      navigate('/tasks', { replace: true });
      return;
    }
    if (user?.role === 'employee' && user?.employeeType === 'accountant') {
      fetchStats();
    }
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      const [hostelsRes, bookingsRes, tasksRes] = await Promise.all([
        api.get('/hostels'),
        api.get('/bookings'),
        api.get('/housekeeping'),
      ]);
      setHostels(hostelsRes.data);
      setBookings(bookingsRes.data);
      setStats({
        hostels: hostelsRes.data.length,
        bookings: bookingsRes.data.length,
        tasks: tasksRes.data.length,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (user?.role === 'employee' && user?.employeeType !== 'accountant') return null;
  if (loading) return null;

  const bookingColumns = [
    { title: 'Guest', dataIndex: 'guestName', key: 'guestName' },
    { title: 'Hostel', dataIndex: ['hostel', 'name'], key: 'hostel', render: (t) => t || 'N/A' },
    { title: 'Check In', dataIndex: 'checkIn', key: 'checkIn', render: (d) => new Date(d).toLocaleDateString() },
    { title: 'Check Out', dataIndex: 'checkOut', key: 'checkOut', render: (d) => new Date(d).toLocaleDateString() },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (s) => <Tag>{s}</Tag> },
  ];

  const quickActions = [
    { label: 'View Hostels', path: '/hostels', icon: <BankOutlined /> },
    { label: 'Manage Bookings', path: '/bookings', icon: <CalendarOutlined /> },
    { label: 'View Tasks', path: '/tasks', icon: <CheckSquareOutlined /> },
    { label: 'Employee Payments', path: '/employee-payments', icon: <WalletOutlined /> },
    { label: 'Bills', path: '/bills', icon: <FileTextOutlined /> },
  ];

  return (
    <>
      <Card title={<span style={{ fontSize: 20, fontWeight: 600 }}>Accountant Dashboard</span>}>
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card><Statistic title="Accessible Hostels" value={stats.hostels} prefix={<BankOutlined />} /></Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card><Statistic title="Total Bookings" value={stats.bookings} prefix={<CalendarOutlined />} /></Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card><Statistic title="Tasks" value={stats.tasks} prefix={<CheckSquareOutlined />} /></Card>
          </Col>
        </Row>

        <Card title="Accessible Hostels" style={{ marginBottom: 24 }} extra={<Button type="primary" onClick={() => navigate('/hostels')}>View All</Button>}>
          {hostels.length === 0 ? (
            <p>No hostels assigned yet.</p>
          ) : (
            <Row gutter={[16, 16]}>
              {hostels.slice(0, 6).map((h) => (
                <Col xs={24} sm={12} md={8} key={h._id}>
                  <Card size="small" hoverable onClick={() => navigate(`/hostels/${h._id}`)} style={{ cursor: 'pointer' }}>
                    <Card.Meta title={h.name} description={`${h.address?.city}, ${h.address?.country} · ${h.rooms?.length || 0} rooms`} />
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Card>

        <Card title="Recent Bookings" style={{ marginBottom: 24 }} extra={<Button type="primary" onClick={() => navigate('/bookings')}>View All</Button>}>
          {bookings.length === 0 ? (
            <p>No bookings found.</p>
          ) : (
            <Table rowKey="_id" columns={bookingColumns} dataSource={bookings.slice(0, 10)} pagination={false} size="small" scroll={{ x: 'max-content' }} />
          )}
        </Card>

        <Card title="Quick Actions">
          <Row gutter={[12, 12]}>
            {quickActions.map((a) => (
              <Col key={a.path}>
                <Button type="default" icon={a.icon} onClick={() => navigate(a.path)}>{a.label}</Button>
              </Col>
            ))}
          </Row>
        </Card>
      </Card>
    </>
  );
};

export default TeamMemberDashboard;
